import React from "react";
import Slider from "react-slick";
import useDimensions from "react-use-dimensions";
import { handleEnterPress, Photo as Image } from "component-library";
import { dataTestIds } from "data-testids";

import { ImageLocation, Photo } from "models";

import { getImageUrl } from "services/UrlBuilder";

import { EmptyData } from "components/common/EmptyData";
import { Overlay } from "components/common/Overlay";
import { heapTrackingForRowGallery } from "app/helpers/heapTracking";

import "./SlickStyles.scss";
import "./RowGallery.scss";

const EMPTY_BLOCK_WIDTH = [
  85, 97, 128, 128, 82, 115, 85, 97, 128, 128, 82, 115, 85, 97, 128, 128, 82,
  115, 85, 97, 128, 128, 82, 115,
];

export interface IRowGalleryProps {
  photos?: Photo[];
  openPhoto: (photo: Photo) => void;
  editGallery: () => void;
  noPhotoText: string;
  hiddenPhotoText: string;
  editGalleryText: string;
  nextButtonText: string;
  prevButtonText: string;
  isEditHidden: boolean;
  galleryPhotoDescription: string;
}

export const RowGallery: React.FC<React.PropsWithChildren<IRowGalleryProps>> = (
  props
) => {
  const [ref, { width: galleryWidth }] = useDimensions();
  const sliderRef = React.useRef<{
    slickNext: () => void;
    slickPrev: () => void;
  }>(null);

  const [showPrevArrow, setShowPrevArrow] = React.useState(false);
  const [showNextArrow, setShowNextArrow] = React.useState(true);
  const [hideAllArrows, setHideAllArrows] = React.useState(false);
  const [totalPhotosWidth, setTotalPhotosWidth] = React.useState(0);

  React.useEffect(() => {
    setHideAllArrows(galleryWidth > totalPhotosWidth);
  }, [galleryWidth, totalPhotosWidth]);

  const onImageLoad = (image: HTMLImageElement) => {
    const width = image.width;
    setTotalPhotosWidth((current) => current + width);
  };

  const beforeChange = React.useCallback(
    (_, next: number) => {
      const amountOfPhoto = props.photos?.length || 0;
      const atEnd = next === amountOfPhoto - 1;
      const atBeginning = next === 0;

      setShowPrevArrow(!atBeginning);
      setShowNextArrow(!atEnd);
    },
    [props.photos]
  );

  const gallerySettings = React.useMemo(
    () => ({
      className: "slider variable-width",
      dots: false,
      infinite: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      variableWidth: true,
      swipeToSlide: true,
      speed: 300,
      arrows: false,
      beforeChange,
    }),
    [beforeChange]
  );

  const nextPhoto = () => sliderRef.current?.slickNext();
  const prevPhoto = () => sliderRef.current?.slickPrev();

  const isPhotos = props.photos && props.photos.length > 0;

  return (
    <div className="c-row-gallery" ref={ref}>
      {!hideAllArrows && isPhotos && (
        <>
          {showPrevArrow && (
            <a
              className="c-row-gallery__button prev icon-chevronleft"
              onClick={prevPhoto}
              onKeyPress={handleEnterPress(prevPhoto)}
              aria-label={props.prevButtonText}
              tabIndex={0}
            />
          )}
          {showNextArrow && (
            <a
              className="c-row-gallery__button next icon-chevronright"
              onClick={nextPhoto}
              onKeyPress={handleEnterPress(nextPhoto)}
              aria-label={props.nextButtonText}
              tabIndex={0}
              role="button"
            />
          )}
        </>
      )}

      {!props.isEditHidden && (
        <a
          className="c-row-gallery__edit-button icon-edit"
          onClick={() => {
            heapTrackingForRowGallery();

            props.editGallery();
          }}
          tabIndex={0}
          onKeyPress={handleEnterPress(props.editGallery)}
          aria-label={props.editGalleryText}
          data-testid={
            dataTestIds.profile["MainPage.RowGallery.RowGallery.editButton"]
          }
          role="button"
        />
      )}

      {!isPhotos ? (
        <div className="c-row-gallery__placeholder">
          {EMPTY_BLOCK_WIDTH.map((width, index) => (
            <div
              key={index}
              className="c-row-gallery__photo-mock"
              style={{ minWidth: width }}
            />
          ))}
          <div className="c-row-gallery__placeholder-label">
            <EmptyData text={props.noPhotoText} theme="light" />
          </div>
        </div>
      ) : (
        <Slider ref={sliderRef} {...gallerySettings}>
          {props.photos?.map((photo) => (
            <div
              key={photo.id}
              className="c-row-gallery__photo-container"
              data-testid={
                dataTestIds.profile["MainPage.RowGallery.RowGallery.photo"]
              }
            >
              <Image
                className="c-row-gallery__photo"
                onClick={() => props.openPhoto(photo)}
                src={getImageUrl(photo.url, ImageLocation.GalleryPhoto)}
                alt={`${props.galleryPhotoDescription} ${
                  photo.description ?? ""
                }`}
                onLoad={onImageLoad}
              />
              {photo.isHidden && (
                <Overlay title={props.hiddenPhotoText} icon="icon-hide" />
              )}
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};
